import React from 'react';
import { motion } from 'framer-motion';
import { Check, Play, Lock, Star } from 'lucide-react';

const QuestIllustration = ({
  quests,
  currentQuestIndex,
  completedQuests = [],
  onQuestClick,
  isInteractive = false
}) => {
  // Configuration for the quest map layout
  // These values are used for both SVG paths and absolute positioning
  const config = {
    viewBoxWidth: 300,
    circleRadius: 32, // Visual radius for SVG calculations (half of 64px mobile circle)
    verticalSpacing: 180, // Distance between node centers (accounts for circle + badges + margin)
    horizontalPadding: 50, // Padding from edges for left/right nodes
  };

  const getQuestStatus = (index) => {
    if (completedQuests.includes(index)) return 'completed';
    if (index === 0) return 'unlocked'; // First quest always unlocked
    if (completedQuests.includes(index - 1)) return 'unlocked'; // Unlocked if previous completed
    return 'locked';
  };

  const getQuestColors = (status) => {
    switch (status) {
      case 'completed':
        return {
          bg: 'bg-gradient-to-br from-emerald-400 to-green-500',
          border: 'border-green-400',
          iconColor: 'text-white',
          textColor: 'text-white'
        };
      case 'unlocked':
        return {
          bg: 'bg-gradient-to-br from-blue-400 to-indigo-500',
          border: 'border-blue-400',
          iconColor: 'text-white',
          textColor: 'text-white'
        };
      case 'locked':
        return {
          bg: 'bg-gradient-to-br from-gray-300 to-gray-400',
          border: 'border-gray-400',
          iconColor: 'text-white',
          textColor: 'text-white'
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-gray-300 to-gray-400',
          border: 'border-gray-300',
          iconColor: 'text-white',
          textColor: 'text-gray-600'
        };
    }
  };

  const getQuestIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Check size={32} strokeWidth={3} />;
      case 'unlocked':
        return <Play size={28} fill="currentColor" />;
      case 'locked':
        return <Lock size={28} />;
      default:
        return <Star size={28} />;
    }
  };

  // Candy Crush style positioning: left -> middle -> right -> middle -> left
  const getPosition = (index) => {
    const pattern = index % 3;
    switch (pattern) {
      case 0:
        return 'left'; // Left side
      case 1:
        return 'center'; // Middle
      case 2:
        return 'right'; // Right side
      default:
        return 'center';
    }
  };

  // Calculate node positions used for both SVG paths and CSS positioning
  const getNodePositions = () => {
    const positions = [];
    const { viewBoxWidth, circleRadius, verticalSpacing, horizontalPadding } = config;

    quests.forEach((_, index) => {
      const position = getPosition(index);
      let x;

      // Calculate X position (as percentage of container width for CSS, absolute for SVG)
      switch (position) {
        case 'left':
          x = horizontalPadding;
          break;
        case 'right':
          x = viewBoxWidth - horizontalPadding;
          break;
        case 'center':
        default:
          x = viewBoxWidth / 2;
          break;
      }

      // Calculate Y position - center of each circle
      const y = circleRadius + (index * verticalSpacing);

      positions.push({
        x,
        y,
        // Convert to percentage for CSS positioning
        xPercent: (x / viewBoxWidth) * 100,
      });
    });

    return positions;
  };

  // Improved SVG Path for connecting line (curved like Candy Crush)
  const getPathBetweenNodes = (startPos, endPos, radius) => {
    // Calculate start and end points at the edge of circles, not center
    // Start from bottom of first circle, end at top of second circle
    const startY = startPos.y + radius;
    const endY = endPos.y - radius;

    // Calculate control points for smooth curve
    const midY = (startY + endY) / 2;
    const controlX1 = startPos.x;
    const controlY1 = midY;
    const controlX2 = endPos.x;
    const controlY2 = midY;

    // Create a smooth cubic bezier curve that connects circle edges
    return `M ${startPos.x} ${startY}
            C ${controlX1} ${controlY1}
              ${controlX2} ${controlY2}
              ${endPos.x} ${endY}`;
  };

  const nodePositions = getNodePositions();
  const { circleRadius, verticalSpacing } = config;
  // Total height in viewBox coordinates
  // Add padding for the badges below the last circle (~60px)
  const totalHeight = quests.length > 0 ? (quests.length - 1) * verticalSpacing + (circleRadius * 2) + 60 : 0;

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-6 py-4 sm:py-8">
      <div
        className="relative"
        style={{ height: totalHeight }}
      >
        {/* SVG for connecting paths */}
        <svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{ zIndex: 0 }}
          viewBox={`0 0 ${config.viewBoxWidth} ${totalHeight}`}
          preserveAspectRatio="none"
        >
          {quests.map((_, index) => {
            if (index >= quests.length - 1) return null;

            const startPos = nodePositions[index];
            const endPos = nodePositions[index + 1];
            const status = getQuestStatus(index);
            const path = getPathBetweenNodes(startPos, endPos, circleRadius);

            // Determine line color based on completion status
            const isCompleted = status === 'completed';
            const strokeColor = isCompleted ? '#10b981' : '#9ca3af';

            return (
              <motion.path
                key={`path-${index}`}
                d={path}
                fill="none"
                stroke={strokeColor}
                strokeWidth="4"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
              />
            );
          })}
        </svg>

        {/* Quest Nodes */}
        {quests.map((quest, index) => {
          const status = getQuestStatus(index);
          const colors = getQuestColors(status);
          const nodePos = nodePositions[index];
          const isClickable = isInteractive && status === 'unlocked' && !completedQuests.includes(index);

          return (
            <div
              key={quest.id || index}
              className="absolute"
              style={{
                left: `${nodePos.xPercent}%`,
                top: nodePos.y,
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}
            >
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.15, type: "spring", bounce: 0.5 }}
                onClick={() => isClickable && onQuestClick?.(index)}
                className={`${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'} relative inline-flex flex-col items-center transition-transform duration-200`}
              >
                {/* Quest Circle with pulse container */}
                <div className="relative inline-flex items-center justify-center">
                  {/* Pulse animation for unlocked quests - positioned behind */}
                  {status === 'unlocked' && !completedQuests.includes(index) && isInteractive && (
                    <motion.div
                      className="absolute inset-0 rounded-full bg-blue-400 -z-10 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24"
                      initial={{ scale: 1, opacity: 0.5 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeOut"
                      }}
                    />
                  )}

                  <motion.div
                    whileHover={isClickable ? { scale: 1.05 } : {}}
                    whileTap={isClickable ? { scale: 0.95 } : {}}
                    className={`
                      w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full
                      ${colors.bg}
                      border-2 border-white/50
                      flex items-center justify-center
                      transition-all duration-300
                      relative
                    `}
                  >
                    <div className={`${colors.iconColor} scale-75 sm:scale-90 md:scale-100`}>
                      {getQuestIcon(status)}
                    </div>

                    {/* Star badge for unlocked quests */}
                    {status === 'unlocked' && !completedQuests.includes(index) && isInteractive && (
                      <motion.div
                        className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center z-10"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                      >
                        <Star size={14} className="text-white" fill="currentColor" />
                      </motion.div>
                    )}

                    {/* Checkmark shine for completed */}
                    {status === 'completed' && (
                      <motion.div
                        className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white to-transparent opacity-30"
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                      />
                    )}
                  </motion.div>
                </div>

                {/* Quest Title Below Circle */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 + 0.2 }}
                  className="mt-2 sm:mt-3 whitespace-nowrap"
                >
                  <div className="bg-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border-2 border-gray-200">
                    <p className="text-[10px] sm:text-xs md:text-sm font-bold text-gray-700 truncate max-w-[80px] sm:max-w-[100px] md:max-w-[120px]">
                      {quest.title || `Quest ${index + 1}`}
                    </p>
                  </div>
                </motion.div>

                {/* Question count badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.15 + 0.3 }}
                  className="mt-1 sm:mt-2 bg-purple-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full border border-purple-600"
                >
                  {quest.questions?.length || 0}Q
                </motion.div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuestIllustration;